import { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// ─── Mock step content ─────────────────────────────────────────────────────

const STEPS = [
  {
    label: 'ASSESS',
    title: 'Self-Assessment',
    subtitle: 'What kind of trader are you?',
    cards: ['New Trader', 'Backtesting', 'Proven'],
  },
  {
    label: 'STRATEGY',
    title: 'Choose Your Weapon',
    subtitle: 'Which model fits your edge?',
    cards: ['TCL Model', 'SMOG Model'],
  },
  {
    label: 'FIRM',
    title: 'Select Your Arena',
    subtitle: 'Pick your prop firm and account.',
    cards: ['Apex', 'Topstep', 'Tradeify', 'MFF'],
  },
  {
    label: 'PROTOCOL',
    title: 'Your Prescription',
    subtitle: 'Armed and ready.',
    cards: [],
    isPrescription: true,
  },
];

// ─── Background gradient colors per step ────────────────────────────────────

const BG_COLORS = [
  { top: '#060A12', mid: '#0A0F1A', bottom: '#060A12' },      // Step 0: deep navy
  { top: '#080B14', mid: '#0F1020', bottom: '#0A0C16' },      // Step 1: slightly warmer
  { top: '#0C0A12', mid: '#1A1018', bottom: '#120D14' },      // Step 2: forge hints
  { top: '#12080A', mid: '#1F0E0A', bottom: '#0A0806' },      // Step 3: forge amber
];

// ─── Parallax layer configs ─────────────────────────────────────────────────

const PARALLAX_SPEEDS = {
  far: 0.15,    // distant layer — barely moves
  mid: 0.4,     // stone texture layer
  content: 1.0, // foreground — full speed
};

const TRANSITION_DISTANCE = 120; // px offset per step change

// ─── Progress Tracker ───────────────────────────────────────────────────────

function StepTracker({ currentStep }: { currentStep: number }) {
  return (
    <div className="flex items-center gap-1 mb-10">
      {STEPS.map((step, i) => {
        const isComplete = i < currentStep;
        const isCurrent = i === currentStep;
        return (
          <div key={step.label} className="flex items-center">
            <motion.div
              className={`
                w-8 h-8 rounded-full flex items-center justify-center text-[10px] font-mono font-medium border
                ${isComplete ? 'bg-[#E8943A]/20 border-[#E8943A]/40 text-[#E8943A]' : ''}
                ${isCurrent ? 'bg-white/10 border-white/20 text-white' : ''}
                ${!isComplete && !isCurrent ? 'bg-white/[0.02] border-white/[0.04] text-white/15' : ''}
              `}
              animate={isCurrent ? { scale: [1, 1.08, 1] } : {}}
              transition={isCurrent ? { repeat: Infinity, duration: 2, ease: 'easeInOut' } : {}}
            >
              {isComplete ? '✓' : i + 1}
            </motion.div>
            {i < STEPS.length - 1 && (
              <motion.div
                className="w-12 h-px mx-1"
                animate={{
                  backgroundColor: isComplete
                    ? 'rgba(232, 148, 58, 0.3)'
                    : 'rgba(255,255,255,0.04)',
                }}
                transition={{ duration: 0.6 }}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Prescription card with border-trace reveal ─────────────────────────────

function PrescriptionCard() {
  return (
    <div className="relative w-full max-w-[480px]">
      {/* SVG border trace */}
      <svg
        className="absolute inset-0 w-full h-full pointer-events-none z-20"
        viewBox="0 0 480 320"
        preserveAspectRatio="none"
      >
        <motion.rect
          x="1"
          y="1"
          width="478"
          height="318"
          rx="12"
          fill="none"
          stroke="#E8943A"
          strokeWidth="1.5"
          strokeDasharray="1596"
          initial={{ strokeDashoffset: 1596 }}
          animate={{ strokeDashoffset: 0 }}
          transition={{ duration: 1.2, ease: 'easeInOut', delay: 0.3 }}
        />
      </svg>

      {/* Card content */}
      <motion.div
        className="relative bg-white/[0.03] border border-white/[0.06] rounded-xl p-8 backdrop-blur-md overflow-hidden"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.8 }}
      >
        {/* Warm glow */}
        <motion.div
          className="absolute inset-0 rounded-xl pointer-events-none"
          animate={{
            boxShadow: [
              '0 0 40px rgba(232, 148, 58, 0.08)',
              '0 0 60px rgba(232, 148, 58, 0.15)',
              '0 0 40px rgba(232, 148, 58, 0.08)',
            ],
          }}
          transition={{ repeat: Infinity, duration: 3, ease: 'easeInOut' }}
        />

        <div className="relative z-10">
          <p className="text-[10px] font-mono text-[#E8943A]/60 tracking-[0.2em] uppercase mb-4">
            Paladin Protocol
          </p>

          <div className="flex items-baseline gap-3 mb-6">
            <span className="text-[11px] font-mono text-white/40 uppercase tracking-wider">
              Risk / Trade
            </span>
            <CountUp target={187} prefix="$" className="text-3xl font-mono font-bold text-white" />
            <CountUp target={1.5} suffix="%" decimals={1} className="text-lg font-mono text-white/50" />
          </div>

          <div className="space-y-3 mb-6">
            {['Max 2 contracts per entry', 'Hard stop at daily loss limit', 'No scaling into losers'].map(
              (rule, i) => (
                <motion.div
                  key={rule}
                  className="flex items-center gap-3"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 1.2 + i * 0.15, duration: 0.4 }}
                >
                  <div className="w-1.5 h-1.5 rounded-full bg-[#E8943A]/60" />
                  <span className="text-sm font-mono text-white/50">{rule}</span>
                </motion.div>
              ),
            )}
          </div>

          <motion.div
            className="h-px bg-gradient-to-r from-transparent via-[#E8943A]/20 to-transparent"
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ delay: 1.6, duration: 0.6 }}
          />

          <motion.p
            className="text-[10px] font-mono text-white/20 mt-4 text-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.8 }}
          >
            Generated Apr 14, 2026. Regenerate if stats change.
          </motion.p>
        </div>
      </motion.div>
    </div>
  );
}

// ─── Counting number animation ──────────────────────────────────────────────

function CountUp({
  target,
  prefix = '',
  suffix = '',
  decimals = 0,
  className = '',
}: {
  target: number;
  prefix?: string;
  suffix?: string;
  decimals?: number;
  className?: string;
}) {
  return (
    <motion.span
      className={className}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 0.9, duration: 0.3 }}
    >
      <motion.span
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        {...({} as any)}
      >
        {prefix}
        <motion.span
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.0 }}
        >
          {target.toFixed(decimals)}
        </motion.span>
        {suffix}
      </motion.span>
    </motion.span>
  );
}

// ─── Step content variants ──────────────────────────────────────────────────

// Content card for non-prescription steps
function StepCard({ label }: { label: string }) {
  return (
    <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl px-6 py-5 backdrop-blur-md">
      <span className="text-sm font-mono text-white/50">{label}</span>
    </div>
  );
}

// ─── Stone texture layer (CSS grain) ────────────────────────────────────────

function StoneLayer({ step }: { step: number }) {
  // Horizontal seam lines that appear progressively
  const seams = Array.from({ length: step + 1 }, (_, i) => (
    <motion.div
      key={i}
      className="absolute left-0 right-0 h-px"
      style={{ top: `${20 + i * 22}%` }}
      initial={{ opacity: 0, scaleX: 0 }}
      animate={{ opacity: 0.06 + step * 0.02, scaleX: 1 }}
      transition={{ duration: 1.2, delay: 0.3 + i * 0.1 }}
    >
      <div className="w-full h-full bg-gradient-to-r from-transparent via-white/30 to-transparent" />
    </motion.div>
  ));

  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden">
      {/* Noise grain */}
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.5'/%3E%3C/svg%3E")`,
          backgroundSize: '256px 256px',
        }}
      />
      {seams}
    </div>
  );
}

// ─── Depth pulse (threshold flash between steps) ────────────────────────────

function DepthPulse({ isActive }: { isActive: boolean }) {
  return (
    <AnimatePresence>
      {isActive && (
        <motion.div
          className="fixed inset-0 z-30 pointer-events-none"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
        >
          <div
            className="w-full h-full"
            style={{
              background:
                'radial-gradient(ellipse at center, rgba(232, 148, 58, 0.06) 0%, transparent 70%)',
            }}
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// ─── Main Preview Component ─────────────────────────────────────────────────

export function TransitionPreview() {
  const [currentStep, setCurrentStep] = useState(0);
  const [direction, setDirection] = useState(1); // 1 = forward, -1 = back
  const [isPulsing, setIsPulsing] = useState(false);
  const [isDarkBeat, setIsDarkBeat] = useState(false);

  const bg = BG_COLORS[currentStep] ?? BG_COLORS[0];
  const step = STEPS[currentStep];

  const goToStep = useCallback(
    (next: number) => {
      if (next < 0 || next >= STEPS.length || next === currentStep) return;

      setDirection(next > currentStep ? 1 : -1);

      // Dark beat for step 1→2 (Assessment → Strategy)
      if (currentStep === 0 && next === 1) {
        setIsDarkBeat(true);
        setTimeout(() => setIsDarkBeat(false), 200);
      }

      // Depth pulse for all transitions
      setIsPulsing(true);
      setTimeout(() => setIsPulsing(false), 250);

      setCurrentStep(next);
    },
    [currentStep],
  );

  // Prescription collapse animation
  const isPrescription = step.isPrescription;

  // Framer Motion variants for step content
  const contentVariants = {
    enter: (dir: number) => ({
      y: dir > 0 ? 80 : -80,
      opacity: 0,
      scale: 0.97,
    }),
    center: {
      y: 0,
      opacity: 1,
      scale: 1,
    },
    exit: (dir: number) => ({
      y: dir > 0 ? -80 : 80,
      opacity: 0,
      scale: 0.97,
    }),
  };

  // Special prescription enter variant
  const prescriptionVariants = {
    enter: {
      scale: 0.6,
      opacity: 0,
    },
    center: {
      scale: 1,
      opacity: 1,
      transition: {
        type: 'spring' as const,
        stiffness: 200,
        damping: 25,
        delay: 0.4,
      },
    },
    exit: {
      scale: 0.8,
      opacity: 0,
    },
  };

  return (
    <div className="relative min-h-screen overflow-hidden select-none">
      {/* ── Layer 0: Background gradient (moves at 0.15x) ── */}
      <motion.div
        className="fixed inset-0 z-0"
        animate={{
          background: `linear-gradient(180deg, ${bg.top} 0%, ${bg.mid} 50%, ${bg.bottom} 100%)`,
        }}
        transition={{ duration: 1.2, ease: 'easeInOut' }}
      />

      {/* Dark beat overlay */}
      <AnimatePresence>
        {isDarkBeat && (
          <motion.div
            className="fixed inset-0 z-20 bg-black"
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.7 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
          />
        )}
      </AnimatePresence>

      {/* ── Layer 1: Stone texture (moves at 0.4x) ── */}
      <motion.div
        className="fixed inset-0 z-[1]"
        animate={{
          y: -currentStep * TRANSITION_DISTANCE * PARALLAX_SPEEDS.mid,
        }}
        transition={{ duration: 0.7, ease: [0.4, 0, 0.2, 1] }}
      >
        <StoneLayer step={currentStep} />
      </motion.div>

      {/* Depth pulse */}
      <DepthPulse isActive={isPulsing} />

      {/* ── Prescription gold flash ── */}
      <AnimatePresence>
        {isPrescription && (
          <motion.div
            className="fixed inset-0 z-25 pointer-events-none"
            initial={{ opacity: 0 }}
            animate={{ opacity: [0, 0.3, 0] }}
            transition={{ duration: 0.5, times: [0, 0.3, 1] }}
          >
            <div className="w-full h-full bg-[#E8943A]/10" />
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Layer 2: Content (moves at 1x) ── */}
      <div className="relative z-10 min-h-screen flex flex-col items-center pt-12 px-8">
        {/* Progress tracker */}
        <StepTracker currentStep={currentStep} />

        {/* Step label */}
        <AnimatePresence mode="wait" custom={direction}>
          <motion.div
            key={`label-${currentStep}`}
            custom={direction}
            initial={{ opacity: 0, y: direction > 0 ? 20 : -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: direction > 0 ? -20 : 20 }}
            transition={{ duration: 0.4, ease: 'easeOut' }}
            className="text-center mb-8"
          >
            <p className="text-[10px] font-mono text-[#E8943A]/50 tracking-[0.3em] uppercase mb-2">
              {step.label}
            </p>
            <h1 className="text-2xl font-mono font-bold text-white/90 tracking-wide">
              {step.title}
            </h1>
            <p className="text-sm font-mono text-white/30 mt-2">{step.subtitle}</p>
          </motion.div>
        </AnimatePresence>

        {/* Step content area */}
        <div className="relative w-full max-w-[520px] min-h-[320px] flex items-center justify-center">
          <AnimatePresence mode="wait" custom={direction}>
            {isPrescription ? (
              <motion.div
                key="prescription"
                variants={prescriptionVariants}
                initial="enter"
                animate="center"
                exit="exit"
                className="w-full flex justify-center"
              >
                <PrescriptionCard />
              </motion.div>
            ) : (
              <motion.div
                key={`step-${currentStep}`}
                custom={direction}
                variants={contentVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{
                  type: 'spring',
                  stiffness: 300,
                  damping: 25,
                  mass: 0.8,
                }}
                className="w-full"
              >
                <div className="grid gap-3">
                  {step.cards.map((card, i) => (
                    <motion.div
                      key={card}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{
                        delay: 0.15 + i * 0.08,
                        type: 'spring',
                        stiffness: 300,
                        damping: 25,
                      }}
                    >
                      <StepCard label={card} />
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Navigation controls */}
        <div className="flex items-center gap-4 mt-12">
          <button
            onClick={() => goToStep(currentStep - 1)}
            disabled={currentStep === 0}
            className={`
              px-5 py-2.5 rounded-lg text-sm font-mono transition-all duration-200
              ${currentStep === 0
                ? 'bg-white/[0.02] text-white/10 border border-white/[0.03] cursor-not-allowed'
                : 'bg-white/[0.04] text-white/50 border border-white/[0.08] hover:bg-white/[0.08] hover:text-white/70'}
            `}
          >
            Back
          </button>

          <div className="text-[10px] font-mono text-white/20 tracking-wider">
            {currentStep + 1} / {STEPS.length}
          </div>

          <button
            onClick={() => goToStep(currentStep + 1)}
            disabled={currentStep === STEPS.length - 1}
            className={`
              px-5 py-2.5 rounded-lg text-sm font-mono transition-all duration-200
              ${currentStep === STEPS.length - 1
                ? 'bg-white/[0.02] text-white/10 border border-white/[0.03] cursor-not-allowed'
                : 'bg-[#E8943A]/10 text-[#E8943A]/80 border border-[#E8943A]/20 hover:bg-[#E8943A]/20 hover:text-[#E8943A]'}
            `}
          >
            {currentStep === STEPS.length - 2 ? 'Reveal Protocol' : 'Next'}
          </button>
        </div>

        {/* Concept label */}
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50">
          <div className="bg-white/[0.03] border border-white/[0.06] rounded-full px-5 py-2 backdrop-blur-md">
            <span className="text-[10px] font-mono text-white/30 tracking-[0.2em] uppercase">
              Concept 1 — The Descent into the Armory
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
